import React from 'react';
import { Switch } from '@/components/ui';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import type { Role } from '@/types';
import { ApplicableRolesField } from './ApplicableRolesField';
import { cn } from '@/lib/utils';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';

interface ApplicableRolesRowProps {
  roles: Role[];
  applicableRoleIds: string[] | null;
  onApplicableRoleIdsChange: (roleIds: string[] | null) => void;
  isCustomized?: boolean;
  context: 'user' | 'client';
  featureName: string;

  // Half-day configuration (optional)
  showHalfDayOption?: boolean;
  applyOnHalfDays?: boolean;
  onApplyOnHalfDaysChange?: (checked: boolean) => void;
  halfDayLabel?: string;
  halfDayDescription?: string;
  className?: string;
}

export const ApplicableRolesRow: React.FC<ApplicableRolesRowProps> = ({
  roles,
  applicableRoleIds,
  onApplicableRoleIdsChange,
  isCustomized,
  context,
  featureName,
  showHalfDayOption = false,
  applyOnHalfDays = false,
  onApplyOnHalfDaysChange,
  halfDayLabel = "Half-Day Shifts",
  halfDayDescription = "Apply this surcharge even when the shift is billed as a half day.",
  className,
}) => {
  // ApplicableRolesField returns null when there's <= 1 role
  const showRoles = (roles?.length ?? 0) > 1;
  const hasVisibleContent = showRoles || showHalfDayOption;

  if (!hasVisibleContent) return null;

  return (
    <div className={cn("mt-4", className)}>
      {showRoles && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 border-t border-dashed border-primary/15" />
            <span className="font-label text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.12em] text-primary shrink-0">Applies To</span>
            <div className="flex-1 border-t border-dashed border-primary/15" />
          </div>
          <ApplicableRolesField
            allRoles={roles}
            selectedRoleIds={applicableRoleIds}
            onSelectionChange={onApplicableRoleIdsChange}
            isCustomized={isCustomized}
            context={context}
            showMobileDivider={false}
          />
        </>
      )}

      <AnimatedCollapse open={showHalfDayOption}>
        {showRoles && (
          <div className="flex items-center gap-2 mt-4 mb-2">
            <div className="flex-1 border-t border-dashed border-border/40" />
            <span className="font-label text-[11px] sm:text-[12px] font-medium uppercase tracking-[0.12em] text-muted-foreground shrink-0">Day Rate Roles</span>
            <div className="flex-1 border-t border-dashed border-border/40" />
          </div>
        )}
        <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center">
          <div className="min-w-0 md:space-y-1">
            <div className="flex items-center gap-2">
              <label
                htmlFor={`${featureName}-apply-on-half-days`}
                className={cn(fieldLabelClass, "whitespace-nowrap")}
              >
                {halfDayLabel}
              </label>
            </div>
            <p className={cn(fieldDescriptionClass, "hidden md:block md:max-w-[44ch]")}>
              {halfDayDescription}
            </p>
          </div>
          <div className="flex items-center justify-end">
            <Switch
              id={`${featureName}-apply-on-half-days`}
              checked={applyOnHalfDays}
              onCheckedChange={onApplyOnHalfDaysChange}
            />
          </div>
          <p className={cn(fieldDescriptionClass, "col-span-2 md:hidden")}>
            {halfDayDescription}
          </p>
        </div>
      </AnimatedCollapse>
    </div>
  );
};
